import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { Minus, Plus, ArrowLeft } from 'lucide-react';
import { API, remapProductImageUrl } from '../lib/api';

const coerceNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const normalizeProduct = (product = {}) => {
    const images = Array.isArray(product.images) ? product.images.filter(Boolean).map(remapProductImageUrl) : [];
    const mainImage = remapProductImageUrl(product.main_image) || (images.length > 0 ? images[0] : null);
    return {
        ...product,
        price: coerceNumber(product.price, 0),
        compare_at_price: product.compare_at_price == null ? null : coerceNumber(product.compare_at_price, 0),
        images,
        main_image: mainImage,
        stock_quantity: coerceNumber(product.stock_quantity, 0),
        related_product_ids: Array.isArray(product.related_product_ids) ? product.related_product_ids : [],
        shipping_cost: product.shipping_cost == null ? null : coerceNumber(product.shipping_cost, 0),
        grade_options: Array.isArray(product.grade_options) ? product.grade_options.filter(Boolean) : [],
        default_grade: product.default_grade || null,
    };
};

const SIZE_CONVERSIONS = {
    oz: { label: 'oz', toOz: v => v },
    quart: { label: 'Quart', toOz: v => v * 32 },
    half_gallon: { label: 'Half Gallon', toOz: v => v * 64 },
    gallon: { label: 'Gallon', toOz: v => v * 128 },
    ml: { label: 'ml', toMl: v => v },
    liter: { label: 'L', toMl: v => v * 1000 },
};

const formatSize = (value, unit) => {
    if (!value || !unit) return null;
    const info = SIZE_CONVERSIONS[unit];
    if (!info) return `${value} ${unit}`;

    let primary = `${value} ${info.label}`;
    let secondary = null;

    if (info.toOz) {
        const totalOz = info.toOz(value);
        if (unit === 'oz') {
            if (totalOz >= 128) secondary = `${(totalOz / 128).toFixed(totalOz % 128 === 0 ? 0 : 1)} Gallon`;
            else if (totalOz >= 64) secondary = `${(totalOz / 64).toFixed(totalOz % 64 === 0 ? 0 : 1)} Half Gallon`;
            else if (totalOz >= 32) secondary = `${(totalOz / 32).toFixed(totalOz % 32 === 0 ? 0 : 1)} Quart`;
            const ml = Math.round(totalOz * 29.5735);
            if (ml >= 1000) secondary = (secondary ? secondary + ' / ' : '') + `${(ml / 1000).toFixed(1)}L`;
            else secondary = (secondary ? secondary + ' / ' : '') + `${ml}ml`;
        } else {
            primary = `${value} ${info.label}`;
            secondary = `${totalOz} oz / ${Math.round(totalOz * 29.5735)}ml`;
        }
    } else if (info.toMl) {
        const totalMl = info.toMl(value);
        const oz = (totalMl / 29.5735).toFixed(1);
        secondary = `${oz} oz`;
    }

    return { primary, secondary };
};

const ProductDetailPage = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const { addItem } = useCart();

    useEffect(() => {
        let isMounted = true;

        const fetchProduct = async () => {
            try {
                const response = await axios.get(`${API}/products/${productId}`);
                const normalized = normalizeProduct(response.data);
                
                if (!isMounted) return;
                
                setProduct(normalized);

                // Fetch related products if any
                if (normalized.related_product_ids && normalized.related_product_ids.length > 0) {
                    try {
                        const allProducts = await axios.get(`${API}/products`);
                        const rawProducts = Array.isArray(allProducts.data) ? allProducts.data : [];
                        const related = rawProducts
                            .map(normalizeProduct)
                            .filter(p => normalized.related_product_ids.includes(p.id));
                        if (isMounted) setRelatedProducts(related);
                    } catch (relErr) {
                        console.error('Error fetching related products:', relErr);
                        if (isMounted) setRelatedProducts([]);
                    }
                } else {
                    if (isMounted) setRelatedProducts([]);
                }

                // Set selected image
                const mainIdx = normalized.main_image 
                    ? normalized.images?.indexOf(normalized.main_image) 
                    : 0;
                if (isMounted) {
                    setSelectedImage(mainIdx >= 0 ? mainIdx : 0);
                    const grades = normalized.grade_options || [];
                    setSelectedGrade(normalized.default_grade || (grades.length > 0 ? grades[0] : null));
                }
            } catch (error) {
                console.error('Error fetching product:', error);
                if (isMounted) {
                    setProduct(null);
                    setRelatedProducts([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setQuantity(1);
                }
            }
        };

        setLoading(true);
        fetchProduct();

        return () => {
            isMounted = false;
        };
    }, [productId]);

    const isOutOfStock = product && (!product.in_stock || (product.track_inventory && product.stock_quantity <= 0));
    const maxQuantity = product?.track_inventory ? product.stock_quantity : 99;

    const handleAddToCart = () => {
        if (product && !isOutOfStock) {
            addItem(product, quantity, { selected_grade: selectedGrade });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <Header />
                <div className="pt-32 pb-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div className="aspect-square bg-maple-card animate-pulse" />
                            <div className="space-y-4">
                                <div className="h-8 bg-maple-card animate-pulse w-1/3" />
                                <div className="h-12 bg-maple-card animate-pulse w-2/3" />
                                <div className="h-24 bg-maple-card animate-pulse" />
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-white">
                <Header />
                <div className="pt-32 pb-24 text-center">
                    <p className="text-maple-dark/60 text-lg mb-6">Product not found</p>
                    <Link to="/shop" className="text-maple-red hover:underline">
                        Back to Shop
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const price = coerceNumber(product.price);
    const compareAtPrice = product.compare_at_price;

    return (
        <div className="min-h-screen bg-white" data-testid="product-detail-page">
            <SEO
                title={product.name}
                description={product.description ? product.description.slice(0, 160) : `Shop ${product.name} — premium maple syrup from Wayside Maple. Crafted in Troy, Pennsylvania.`}
                keywords={`${product.name}, maple syrup, Wayside Maple, buy maple syrup`}
                path={`/shop/${productId}`}
                type="product"
            />
            <Header />
            
            <section className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Link 
                        to="/shop" 
                        className="inline-flex items-center gap-2 text-maple-dark/60 hover:text-maple-dark mb-8 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="font-manrope text-sm uppercase tracking-wider">Back to Shop</span>
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div>
                            <div className="aspect-square bg-maple-secondary border border-black/10 overflow-hidden mb-4">
                                {product.images && product.images.length > 0 ? (
                                    <img 
                                        src={product.images[selectedImage]} 
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-black/20">
                                        <span className="font-mono">No Image</span>
                                    </div>
                                )}
                            </div>
                            {product.images && product.images.length > 1 && (
                                <div className="flex gap-4">
                                    {product.images.map((img, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImage(index)}
                                            className={`w-20 h-20 border overflow-hidden transition-all ${
                                                index === selectedImage 
                                                    ? 'border-maple-red' 
                                                    : 'border-black/10 hover:border-black/30'
                                            }`}
                                        >
                                            <img 
                                                src={img} 
                                                alt={`${product.name} ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            {product.featured && (
                                <span className="inline-block bg-maple-red px-3 py-1 mb-4">
                                    <span className="font-mono text-xs text-white uppercase tracking-wider">Featured</span>
                                </span>
                            )}
                            
                            <span className="text-maple-dark/40 text-sm font-mono uppercase tracking-wider">
                                {product.category}
                            </span>
                            
                            <h1 className="font-anton text-4xl sm:text-5xl text-maple-dark mt-2">
                                {product.name}
                            </h1>

                            {product.size_value && product.size_unit && (() => {
                                const sizeInfo = formatSize(product.size_value, product.size_unit);
                                return sizeInfo && (
                                    <p className="text-maple-dark/50 text-sm font-mono mt-2" data-testid="product-size-info">
                                        {sizeInfo.primary}{sizeInfo.secondary && <span className="text-maple-dark/30"> ({sizeInfo.secondary})</span>}
                                    </p>
                                );
                            })()}
                            
                            <div className="mt-4 flex items-baseline gap-3" data-testid="product-price-area">
                                <span className="font-mono text-3xl text-maple-dark">${price.toFixed(2)}</span>
                                {compareAtPrice !== null && compareAtPrice > price && (
                                    <span className="font-mono text-lg text-maple-dark/40 line-through" data-testid="compare-at-price">
                                        ${compareAtPrice.toFixed(2)}
                                    </span>
                                )}
                                {compareAtPrice !== null && compareAtPrice > price && (
                                    <span className="text-xs font-manrope uppercase tracking-wider bg-maple-red/10 text-maple-red px-2 py-1" data-testid="discount-badge">
                                        Save {Math.round((1 - price / compareAtPrice) * 100)}%
                                    </span>
                                )}
                            </div>

                            {relatedProducts.length > 0 && (
                                <div className="mt-6" data-testid="related-sizes">
                                    <label className="text-maple-dark/40 text-xs font-mono uppercase tracking-wider block mb-2">Available Sizes</label>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-4 py-2 border-2 border-maple-red bg-maple-red/5 text-maple-dark text-sm font-mono" data-testid="current-size">
                                            {product.size_value && product.size_unit
                                                ? `${product.size_value} ${SIZE_CONVERSIONS[product.size_unit]?.label || product.size_unit}`
                                                : product.name} — ${price.toFixed(2)}
                                        </span>
                                        {relatedProducts.map(rp => (
                                            <Link
                                                key={rp.id}
                                                to={`/shop/${rp.id}`}
                                                className="px-4 py-2 border border-black/10 hover:border-maple-red text-maple-dark/70 hover:text-maple-dark text-sm font-mono transition-colors"
                                                data-testid={`related-size-${rp.id}`}
                                            >
                                                {rp.size_value && rp.size_unit
                                                    ? `${rp.size_value} ${SIZE_CONVERSIONS[rp.size_unit]?.label || rp.size_unit}`
                                                    : rp.name} — ${coerceNumber(rp.price).toFixed(2)}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-8 pt-8 border-t border-black/10">
                                <p className="text-maple-dark/70 leading-relaxed">
                                    {product.description}
                                </p>
                            </div>

                            {product.grade_options && product.grade_options.length > 0 && (
                                <div className="mt-6" data-testid="grade-selector">
                                    <label className="text-maple-dark/40 text-xs font-mono uppercase tracking-wider block mb-2">Syrup Grade</label>
                                    <select
                                        value={selectedGrade || ''}
                                        onChange={e => setSelectedGrade(e.target.value)}
                                        className="w-full border border-black/10 bg-white px-4 py-3 text-maple-dark font-mono focus:border-maple-red outline-none"
                                    >
                                        {product.grade_options.map(grade => (
                                            <option key={grade} value={grade}>{grade}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {product.sku && (
                                <p className="text-maple-dark/40 text-sm font-mono mt-6">
                                    SKU: {product.sku}
                                </p>
                            )}

                            <div className="mt-8 pt-8 border-t border-black/10">
                                {product.track_inventory && !isOutOfStock && (
                                    <p className="text-maple-dark/50 text-sm font-mono mb-4" data-testid="stock-info">
                                        {product.stock_quantity <= product.low_stock_threshold
                                            ? `Only ${product.stock_quantity} left in stock!`
                                            : `${product.stock_quantity} in stock`}
                                    </p>
                                )}
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            disabled={isOutOfStock}
                                            className="w-10 h-10 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark disabled:opacity-50"
                                            data-testid="detail-decrease-qty"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="font-mono w-10 text-center text-lg text-maple-dark">{quantity}</span>
                                        <button
                                            onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                                            disabled={isOutOfStock}
                                            className="w-10 h-10 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark disabled:opacity-50"
                                            data-testid="detail-increase-qty"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <button
                                        onClick={handleAddToCart}
                                        disabled={isOutOfStock}
                                        className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="detail-add-to-cart"
                                    >
                                        <span>{isOutOfStock ? 'Out of Stock' : 'Add to Cart'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ProductDetailPage;
